import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Autocomplete,
  Alert,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useGetClassesQuery } from "@/store/api/adminApi";
import { CLASSES_PAGE_SIZE } from "@/constants/examConstants";
import useDebounce from "@/hooks/useDebounce";

// Distance (px) from the bottom of the dropdown at which the next page is loaded
const LOAD_MORE_THRESHOLD = 40;

/**
 * Multi-select for classes that loads them from the server page by page.
 * The next page is fetched when the user scrolls to the bottom of the dropdown,
 * and typing searches classes on the server.
 *
 * `value` / `onChange` work with class objects (not ids) so selected chips
 * keep their labels even when the class is not in the currently loaded pages.
 */
const ClassMultiSelect = ({
  value,
  onChange,
  excludeIds = [],
  emptyMessage = "All classes are already assigned",
  chipColor = "#e3f2fd",
}) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue.trim(), 400);
  const [query, setQuery] = useState({ page: 1, search: "" });
  const [loadedClasses, setLoadedClasses] = useState([]);

  const { currentData, isLoading, isFetching, error } = useGetClassesQuery({
    page: query.page,
    limit: CLASSES_PAGE_SIZE,
    search: query.search,
  });

  // A new search starts again from the first page
  useEffect(() => {
    setQuery((prev) =>
      prev.search === debouncedSearch ? prev : { page: 1, search: debouncedSearch }
    );
  }, [debouncedSearch]);

  // Accumulate the loaded pages into a single list
  useEffect(() => {
    const classes = currentData?.data?.classes;
    if (!classes) return;
    if (query.page === 1) {
      setLoadedClasses(classes);
    } else {
      setLoadedClasses((prev) => {
        const loadedIds = new Set(prev.map((c) => c._id));
        return [...prev, ...classes.filter((c) => !loadedIds.has(c._id))];
      });
    }
  }, [currentData, query.page]);

  const totalPages = currentData?.data?.pagination?.pages || 1;
  const hasMore = query.page < totalPages;

  const options = useMemo(() => {
    const excluded = new Set(excludeIds);
    return loadedClasses.filter((c) => !excluded.has(c._id));
  }, [loadedClasses, excludeIds]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || isFetching) return;
    setQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  }, [hasMore, isFetching]);

  // When most of a page is excluded (already assigned), the dropdown can be too
  // short to scroll, so keep loading until a full page of options is available
  useEffect(() => {
    if (currentData && options.length < CLASSES_PAGE_SIZE) {
      loadNextPage();
    }
  }, [currentData, options.length, loadNextPage]);

  const handleListboxScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD) {
        loadNextPage();
      }
    },
    [loadNextPage]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load classes</Alert>;
  }

  if (!query.search && !hasMore && !isFetching && options.length === 0) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        // Keep the search text when an option is selected
        if (reason !== "reset") setInputValue(newInputValue);
      }}
      // Filtering is done on the server
      filterOptions={(x) => x}
      isOptionEqualToValue={(option, selected) => option._id === selected._id}
      getOptionLabel={(option) => option.classCode || option.className || ""}
      loading={isFetching}
      loadingText="Loading classes..."
      noOptionsText="No classes found"
      ListboxProps={{
        onScroll: handleListboxScroll,
        style: { maxHeight: 250 },
      }}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {option.classCode}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.className || `${option.studentCount || 0} students`}
            </Typography>
          </Box>
        </li>
      )}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            label={option.classCode || option.className}
            size="small"
            sx={{ bgcolor: chipColor }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Classes"
          placeholder="Search classes..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ mb: 2 }}
    />
  );
};

export default ClassMultiSelect;
